import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelected } from 'client/store/mapSlice/mapSlice';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { MapContext } from 'os-elements';
import EnhancedMeasuresList from 'client/pages/MapPage/components/MapMenu/EnhancedMeasures/EnhancedMeasuresList/EnhancedMeasuresList';
import * as styleRuleEvaluator from 'client/utils/evaluators/styleRuleEvaluator';
import { styleRuleToIcon } from 'client/pages/MapPage/components/Legend/Legend.utils';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import { selectSectorSpecificStyles } from 'client/store/layersSlice/layersSlice';

const enhancedMeasuresSource = new VectorSource();
const enhancedMeasuresLayer = new VectorLayer({
    source: enhancedMeasuresSource
});

const EnhancedMeasures = () => {
    const { map } = useContext(MapContext);
    const dispatch = useDispatch();
    const { layers, sensitiveAssets } = useSelector(state => state.assets);
    const mapConfig = useSelector(state => state.mapConfig);
    const layerStyles = useSelector(state => state.layers?.result?.styles);
    const supplementaryData = useSelector(state => state.supplementaryData.data);
    const openFeatures = useSelector(state => state.map?.selected || []);
    const currentLocale = useSelector(state => state.locale?.current);
    const sectorSpecificStyles = useSelector(selectSectorSpecificStyles);

    useEffect(() => {
        dispatch(setSelected([]));
        enhancedMeasuresLayer.setMap(map);
        return () => {
            dispatch(setSelected([]));
            enhancedMeasuresLayer.setMap(null);
        };
    }, []);

    useEffect(() => {
        enhancedMeasuresSource.clear();

        if (!openFeatures) {
            return;
        }

        if (openFeatures.length) {
            enhancedMeasuresSource.addFeatures(openFeatures);
        }
    }, [openFeatures]);

    const allFeatures = Object.keys(layers).map(key => layers[key]).flat();

    const featureMap = allFeatures.reduce((acc, feature) => {
        const siteId = feature?.getProperties()?.dataproviderassigneduniqueid;
        if (siteId) {
            acc[siteId] = feature;
        }
        return acc;
    }, {});

    function getFeatureForAssetId (siteId) {
        return featureMap[siteId];
    }

    const enhancedMeasures = sensitiveAssets.map(sensitiveAssetData => {
        const id = sensitiveAssetData.dataproviderassigneduniqueid;
        const site = {
            id,
            nuarActor: supplementaryData[id]?.nuarActor,
            contacts: supplementaryData[id]?.contacts,
            sensitiveAssetData
        };

        const feature = getFeatureForAssetId(site.id);
        if (!feature) {
            return site;
        }

        const featureId = feature.getId();
        site.feature = feature.clone();
        site.feature.setId(featureId);

        const assetType = featureId.split('.')[0];
        const layerConfig = mapConfig?.result.layers.find(layer => layer.assetType === assetType);
        site.layerName = layerConfig?.displayName[currentLocale];

        const styleRule = styleRuleEvaluator.evaluateStyleRules(site.feature, layerConfig?.styleRules, sectorSpecificStyles);
        site.assetName = styleRule?.displayName[currentLocale];
        const styleId = styleRule?.selectedStyleId || styleRule?.styleId;
        const selectedStyle = layerStyles[styleId];
        const rotatedStyle = styleRule?.rotationPropertyName
            ? styleRuleEvaluator.evaluateRotation(styleRule, site.feature, selectedStyle)
            : selectedStyle;
        site.feature.setStyle(rotatedStyle);

        const style = mapConfig?.result?.styles?.find(style => style.id === styleId);
        site.icon = style ? styleRuleToIcon(style) : null;
        return site;
    });

    return (
        <EnhancedMeasuresList
            data-testid={'enhancedMeasuresList'}
            enhancedMeasures={enhancedMeasures}
        />
    );
};

EnhancedMeasures.propTypes = {
    ...defaultAriaProps
};

export default EnhancedMeasures;
